import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { CompTopicExampleRow, OverviewFilters } from "@nexus/contracts";
import type { CompetitorFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  buildCompTopicDetailExamplesSearch,
  compTopicDetailExamplesInfiniteQueryOptions,
  flattenExampleBatches,
} from "@/lib/queries/overview.queries";
import type { CompTopicDetailContext } from "../lib/compTopicDetailContext.js";

export function useCompTopicDetailExamples(
  tenantId: string,
  overviewFilters: OverviewFilters,
  competitorFilters: CompetitorFiltersInput,
  context: CompTopicDetailContext | null,
  enabled: boolean,
) {
  const querySearch = context
    ? buildCompTopicDetailExamplesSearch(overviewFilters, competitorFilters, context)
    : "";

  const query = useInfiniteQuery({
    ...compTopicDetailExamplesInfiniteQueryOptions(
      tenantId,
      overviewFilters,
      competitorFilters,
      context,
    ),
    enabled: enabled && !!tenantId && !!context,
  });

  const data = useMemo(
    () => flattenExampleBatches<CompTopicExampleRow>(query.data?.pages),
    [query.data?.pages],
  );

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    querySearch,
  };
}

/** Drawer-level selected example index — resets when topic+bucket changes. */
export function useCompTopicDetailExampleIndex(context: CompTopicDetailContext | null) {
  const resetKey = context ? `${context.topicTitle}:${context.bucket}` : "";
  const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);

  useEffect(() => {
    setSelectedExampleIndex(0);
  }, [resetKey]);

  return [selectedExampleIndex, setSelectedExampleIndex] as const;
}
